import { StripeSubscriptionScheduleResponse } from "@switcherstudio/api-core-client";
import { SilverSunnStripeSubscription } from "@switcherstudio/switcher-api-client";
import { useSwitcherClientCore } from "./useSwitcherClient";
import { usePauseStatus } from "./usePauseStatus";
import { useCallback } from "react";

export enum PauseAction {
    Pause = "Pause",
    UpdatePause = "Update",
    CancelPauseBefore = "CancelPauseBefore",
    CancelPauseDuring = "CancelPauseDuring",
    CancelPlan = "CancelPlan"
}

export interface usePauseOptions {
    currentSubscription: SilverSunnStripeSubscription;
    subscriptionSchedule?: StripeSubscriptionScheduleResponse | undefined;
    planId: string;
    pauseDate: string;
    onPause: () => void;
    onPauseUpdate: () => void;
    onCancelPause?: (pauseAction: PauseAction) => void;
    onCancelPlan?: () => void;
    onError?: (error: any, pauseAction: PauseAction) => void;
}

export const usePause = ({
    currentSubscription,
    subscriptionSchedule,
    planId,
    pauseDate,
    onPause,
    onPauseUpdate,
    onCancelPause,
    onCancelPlan,
    onError
}: usePauseOptions) => {
    const { userWillPause, userIsOrWillPause } = usePauseStatus();

    const { loading: creatingSchedule, dispatchApiRequest: createSchedule } =
        useSwitcherClientCore((client) => client.stripe_CreateScheduleById, {
            suppressErrorBoundary: true,
            requestImmediately: false
        });
    const { loading: updatingSchedule, dispatchApiRequest: updateSchedule } =
        useSwitcherClientCore((client) => client.stripe_UpdateScheduleById, {
            suppressErrorBoundary: true,
            requestImmediately: false
        });

    const { loading: releasingSchedule, dispatchApiRequest: releaseSchedule } =
        useSwitcherClientCore((client) => client.stripe_ReleaseScheduleById, {
            suppressErrorBoundary: true,
            requestImmediately: false
        });

    const { loading: deletingSchedule, dispatchApiRequest: deleteSchedule } =
        useSwitcherClientCore((client) => client.stripe_DeleteScheduleById, {
            suppressErrorBoundary: true,
            requestImmediately: false
        });

    const createPause = useCallback(async () => {
        if (userIsOrWillPause) return;

        try {
            await createSchedule([
                {
                    Details: {
                        EndDate: pauseDate,
                        PlanId: planId,
                        StripeSubscriptionId:
                            currentSubscription?.SilverSunnStripeSubscriptionId
                    }
                }
            ]);
            onPause?.();
        } catch (error) {
            onError?.(error, PauseAction.Pause);
        }
    }, [
        createSchedule,
        currentSubscription,
        pauseDate,
        planId,
        userIsOrWillPause,
        onPause,
        onError
    ]);

    const updatePause = useCallback(async () => {
        if (!userIsOrWillPause) return;

        try {
            await updateSchedule([
                subscriptionSchedule?.Details.Id || "",
                {
                    Details: {
                        EndDate: pauseDate,
                        PlanId: planId,
                        Id: subscriptionSchedule?.Details.Id
                    }
                }
            ]);
            onPauseUpdate?.();
        } catch (error) {
            onError?.(error, PauseAction.UpdatePause);
        }
    }, [
        updateSchedule,
        subscriptionSchedule,
        pauseDate,
        planId,
        userIsOrWillPause,
        onPauseUpdate,
        onError
    ]);

    const cancelPause = useCallback(async () => {
        try {
            await releaseSchedule([subscriptionSchedule?.Details.Id || ""]);
            onCancelPause?.(
                userWillPause
                    ? PauseAction.CancelPauseBefore
                    : PauseAction.CancelPauseDuring
            );
        } catch (error) {
            onError?.(
                error,
                userWillPause
                    ? PauseAction.CancelPauseBefore
                    : PauseAction.CancelPauseDuring
            );
        }
    }, [
        releaseSchedule,
        subscriptionSchedule,
        userWillPause,
        onCancelPause,
        onError
    ]);

    const cancelPlan = useCallback(async () => {
        if (!userIsOrWillPause) return;

        try {
            await deleteSchedule([subscriptionSchedule?.Details.Id || ""]);
            onCancelPlan?.();
        } catch (error) {
            onError?.(error, PauseAction.CancelPlan);
        }
    }, [
        deleteSchedule,
        onCancelPlan,
        subscriptionSchedule,
        userIsOrWillPause,
        onError
    ]);

    return {
        /**
         * Creates a pause schedule for the current subscription
         */
        createPause,
        /**
         * Updates a pause schedule for the current subscription
         */
        updatePause,
        /**
         * Cancels the pause before it starts, releasing the schedule
         */
        cancelPause,
        /**
         * Cancels the plan, deleting the schedule and canceling the plan
         */
        cancelPlan,
        /**
         * Any of the pause-related actions are currently loading
         */
        loading:
            creatingSchedule ||
            updatingSchedule ||
            releasingSchedule ||
            deletingSchedule
    };
};
